/**
 * Products API client (Sub-Plan 4 kengaytirilgan)
 *
 *   GET    /products?page=&limit=&search=&status=...
 *   POST   /products
 *   GET    /products/empty-codes
 *   GET    /products/history
 *   DELETE /products/history
 *   DELETE /products/history/:id
 *   POST   /products/bulk-category
 *   POST   /products/bulk-delete
 *   GET    /products/:id
 *   PATCH  /products/:id
 *   DELETE /products/:id
 */

import type {
  ApiSuccess,
  BulkCategoryUpdateInput,
  BulkDeleteInput,
  BulkDeleteResponse,
  BulkJobStartResult,
  BulkJobStatus,
  BulkPublishResult,
  CreateProductInput,
  EmptyCodesQueryInput,
  FinancialSummary,
  MarketplacePublishStatus,
  ProductDto,
  ProductHistoryEntry,
  ProductHistoryQueryInput,
  ProductQueryInput,
  ProductVariant,
  SkuListResult,
  SkuRangeInfo,
  UpdateProductInput,
} from '@avtofix/shared';

import { apiClient } from '@/shared/lib/api-client';

export const productsApi = {
  list: async (query: Partial<ProductQueryInput> = {}): Promise<ApiSuccess<ProductDto[]>> => {
    return apiClient.getPaginated<ProductDto>('/products', { params: query });
  },

  getOne: async (id: string): Promise<ProductDto> => {
    return apiClient.get<ProductDto>(`/products/${id}`);
  },

  /** Moliyaviy hisobot — barcha aktiv mahsulotlar (server-side aggregation) */
  financialSummary: async (): Promise<FinancialSummary> => {
    return apiClient.get<FinancialSummary>('/products/financial-summary');
  },

  create: async (input: CreateProductInput): Promise<ProductDto> => {
    return apiClient.post<ProductDto>('/products', input);
  },

  update: async (id: string, input: UpdateProductInput): Promise<ProductDto> => {
    return apiClient.patch<ProductDto>(`/products/${id}`, input);
  },

  delete: async (id: string): Promise<{ id: string }> => {
    return apiClient.delete<{ id: string }>(`/products/${id}`);
  },

  // ─── Sub-Plan 4 ────────────────────────────────────────────────

  findEmptyCodes: async (query: Partial<EmptyCodesQueryInput> = {}): Promise<number[]> => {
    return apiClient.get<number[]>('/products/empty-codes', { params: query });
  },

  /** Yangi mahsulot uchun navbatdagi ketma-ket SKU (eng katta + 1, gap-fill emas). */
  getNextSku: async (): Promise<number> => {
    const res = await apiClient.get<{ sku: number }>('/products/next-sku');
    return res.sku;
  },

  listHistory: async (
    query: Partial<ProductHistoryQueryInput> = {},
  ): Promise<ApiSuccess<ProductHistoryEntry[]>> => {
    return apiClient.getPaginated<ProductHistoryEntry>('/products/history', { params: query });
  },

  clearHistory: async (): Promise<{ deleted: number }> => {
    return apiClient.delete<{ deleted: number }>('/products/history');
  },

  deleteHistoryEntry: async (id: string): Promise<{ id: string }> => {
    return apiClient.delete<{ id: string }>(`/products/history/${id}`);
  },

  bulkUpdateCategory: async (input: BulkCategoryUpdateInput): Promise<{ updated: number }> => {
    return apiClient.post<{ updated: number }>('/products/bulk-category', input);
  },

  bulkDelete: async (input: BulkDeleteInput): Promise<BulkDeleteResponse> => {
    return apiClient.post<BulkDeleteResponse>('/products/bulk-delete', input);
  },

  findBySkuRange: async (minSku: number, maxSku: number): Promise<ProductDto[]> => {
    return apiClient.get<ProductDto[]>('/products/by-sku-range', {
      params: { minSku, maxSku },
    });
  },

  /** Aniq SKU kodlar ro'yxati bo'yicha mahsulotlar (bulk etiketka — aralash kodlar) */
  findBySkuList: async (skus: string[]): Promise<SkuListResult> => {
    return apiClient.post<SkuListResult>('/products/by-sku-list', { skus });
  },

  /** SKU taqsimoti — bulk modal hint (mavjud raqamli oraliq) uchun */
  skuRangeInfo: async (): Promise<SkuRangeInfo> => {
    return apiClient.get<SkuRangeInfo>('/products/sku-range');
  },

  checkSku: async (
    value: string,
    field: 'sku' | 'barcode',
    excludeId?: string,
  ): Promise<{ available: boolean }> => {
    return apiClient.get<{ available: boolean }>('/products/check-sku', {
      params: { value, field, ...(excludeId !== undefined ? { excludeId } : {}) },
    });
  },

  /**
   * POS skaner — parent va variant ichida exact match (Faza POS-Variant 2026-05-21).
   * Priority: customId > barcode/barcodeId > sku > code > catalogNumber.
   */
  scanByCode: async (code: string): Promise<ScanResult> => {
    return apiClient.get<ScanResult>(`/products/scan/${encodeURIComponent(code)}`);
  },

  // ─── Marketplace (bir-bosqichli publish) ───────────────────────
  /** Mahsulotni marketplace'ga chiqarish (darhol jonli). Narx berilmasa product.price. */
  publishToMarketplace: async (
    id: string,
    marketplacePrice?: number,
  ): Promise<MarketplacePublishResult> => {
    return apiClient.post<MarketplacePublishResult>(
      `/marketplace/products/${id}/publish-now`,
      marketplacePrice !== undefined ? { marketplacePrice } : {},
    );
  },

  /** Mahsulotni marketplace'dan olib tashlash. */
  unpublishFromMarketplace: async (id: string): Promise<MarketplacePublishResult> => {
    return apiClient.post<MarketplacePublishResult>(`/marketplace/products/${id}/unpublish`, {});
  },

  /**
   * KO'P mahsulotni birdan marketplace'ga chiqarish (tanlanganlar).
   * Server ketma-ket ishlaydi → uzun bo'lishi mumkin: timeout 180s (default 30s emas).
   */
  bulkPublishToMarketplace: async (ids: string[]): Promise<BulkPublishResult> => {
    return apiClient.post<BulkPublishResult>(
      '/marketplace/products/bulk-publish',
      { ids },
      { timeout: 180_000 },
    );
  },

  /** Butun katalogni bitta amal bilan marketplace'ga (server FONDA ishlaydi → darhol qaytadi). */
  publishAllToMarketplace: async (): Promise<BulkJobStartResult> => {
    return apiClient.post<BulkJobStartResult>('/marketplace/products/publish-all', {});
  },

  /** Butun katalogni bitta amal bilan marketplace'dan OLIB TASHLASH (server FONDA). */
  unpublishAllFromMarketplace: async (): Promise<BulkJobStartResult> => {
    return apiClient.post<BulkJobStartResult>('/marketplace/products/unpublish-all', {});
  },

  /** "Hammasini" fon ishining holati (progress polling — N/total, xato soni). */
  bulkJobStatus: async (): Promise<BulkJobStatus> => {
    return apiClient.get<BulkJobStatus>('/marketplace/products/bulk-job');
  },
};

export interface MarketplacePublishResult {
  productId: string;
  marketplaceStatus: MarketplacePublishStatus;
}

// ─── POS-Variant: scan natijasi (discriminated union) ────────────
export type ScanResult =
  | { found: false }
  | { found: true; type: 'parent'; product: ProductDto }
  | {
      found: true;
      type: 'variant';
      product: ProductDto;
      variantIndex: number;
      variant: ProductVariant;
    };
